import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { FiSave, FiX } from 'react-icons/fi';
import Button from '../components/ui/Button';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import { parseLocalizedNumber } from '../utils/numberFormat';
import toast from 'react-hot-toast';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';

function GastosForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sitios, setSitios] = useState([]);

  const [form, setForm] = useState({
    concepto: '',
    monto: '',
    fecha: new Date().toISOString().split('T')[0],
    sitio_id: '',
    observaciones: '',
  });

  useEffect(() => {
    if (profile?.organization_id) {
      fetchSitios();
      if (id) fetchGasto();
    }
  }, [id, profile]);

  useEffect(() => {
    if (form.sitio_id) persistSelectedSiteId(form.sitio_id);
  }, [form.sitio_id]);

  async function fetchSitios() {
    const { data } = await supabase.from('sitios').select('*').eq('organization_id', profile.organization_id);
    const availableSites = data || [];
    setSitios(availableSites);
    if (!id) {
      setForm((prev) => ({
        ...prev,
        sitio_id: resolvePreferredSiteId(availableSites, {
          currentSiteId: prev.sitio_id,
        }),
      }));
    }
  }

  async function fetchGasto() {
    const { data } = await supabase.from('gastos').select('*').eq('id', id).single();
    if (data) {
      setForm({
        ...data,
        monto: data.monto.toString(),
        observaciones: data.observaciones || '',
      });
    }
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile?.organization_id) return toast.error('Error: sesion no valida');

    setLoading(true);
    try {
      const monto = parseLocalizedNumber(form.monto);
      if (!monto || monto <= 0) {
        throw new Error('Ingresa un monto valido');
      }

      const payload = {
        ...form,
        monto,
        organization_id: profile.organization_id,
      };

      const { error } = id
        ? await supabase.from('gastos').update(payload).eq('id', id)
        : await supabase.from('gastos').insert([payload]);

      if (error) throw new Error(error.message);

      toast.success(id ? 'GASTO ACTUALIZADO' : 'GASTO REGISTRADO');
      navigate('/sistema/gastos');
    } catch (error) {
      toast.error(`Error al guardar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!profile?.organization_id) {
    return <div className="p-20 text-center font-black tracking-widest text-slate-400 animate-pulse">VERIFICANDO ORGANIZACION...</div>;
  }

  return (
    <div className="gastify-form-shell flex items-start justify-center md:pt-10">
      <div className="gastify-form-card max-w-4xl">
        <div className="gastify-form-header">
          <div>
            <h2 className="gastify-form-title">{id ? 'Editar gasto' : 'Nuevo registro de gasto'}</h2>
            <p className="gastify-form-subtitle">Carga operativa con fecha, sitio y observaciones complementarias</p>
          </div>
          <button type="button" onClick={() => navigate('/sistema/gastos')} className="gastify-icon-button">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSave} className="gastify-form-body space-y-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="gastify-section md:col-span-2">
              <label className="gastify-label">Concepto o categoria</label>
              <input
                type="text"
                className="input-impulza"
                value={form.concepto}
                onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                placeholder="Ej: Alquiler, luz, internet..."
                required
              />
            </div>

            <div className="gastify-section">
              <label className="gastify-label">Monto ($)</label>
              <LocalizedNumberInput
                decimals={2}
                selectOnFocus
                className="input-impulza font-black text-red-500"
                value={form.monto}
                onValueChange={(display) => setForm({ ...form, monto: display })}
                placeholder="0,00"
                required
              />
            </div>

            <div className="gastify-section">
              <label className="gastify-label">Fecha</label>
              <input
                type="date"
                className="input-impulza"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                required
              />
            </div>

            <div className="gastify-section md:col-span-2">
              <label className="gastify-label">Sitio o sucursal</label>
              <select
                className="input-impulza"
                value={form.sitio_id}
                onChange={(e) => setForm({ ...form, sitio_id: e.target.value })}
                required
              >
                <option value="">Seleccione sitio...</option>
                {sitios.map((sitio) => (
                  <option key={sitio.id} value={sitio.id}>
                    {sitio.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="gastify-section md:col-span-2">
              <label className="gastify-label">Observaciones adicionales</label>
              <textarea
                className="input-impulza h-24 resize-none"
                value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                placeholder="Detalles sobre el gasto, numero de factura o comentarios internos..."
              />
            </div>
          </div>

          <Button type="submit" isLoading={loading} iconLeft={<FiSave />} className="btn-impulza w-full !py-5">
            {id ? 'Actualizar gasto' : 'Confirmar gasto'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default GastosForm;
